import { CurrencyAmount } from '@kodiak-finance/sdk-core'
import { POSITION_BADGES } from 'config/portfolio'
import { ROUTES } from 'config/routes'
import { useMemo } from 'react'
import { ProcessedV3Position } from 'types/subgraph'
import {
  calculateFeeUSDValue,
  calculateV3PositionValue,
  formatTokenAmountWithUSD,
  formatV3PositionTokenAmounts,
  isV3PositionInRange,
} from 'utils/positionHelpers'
import { Address } from 'viem'

interface UseV3PositionDisplayParams {
  position: ProcessedV3Position
  fees?: {
    amount0: string
    amount1: string
    error?: string
  }
  tokenPrices?: Record<Address, number>
}

export const useV3PositionDisplay = ({ position, fees, tokenPrices }: UseV3PositionDisplayParams) => {
  const { positionValue, token0Display, token1Display, feeData } = useMemo(() => {
    const value = calculateV3PositionValue(position, tokenPrices)
    const { token0Display: t0, token1Display: t1 } = formatV3PositionTokenAmounts(position, tokenPrices)

    const fee0Amount = fees?.amount0 || position.uncollectedFeesToken0
    const fee1Amount = fees?.amount1 || position.uncollectedFeesToken1

    const token0 = position.sdkPosition.pool.token0
    const token1 = position.sdkPosition.pool.token1

    const feeValue0 =
      fee0Amount && fee0Amount !== '0'
        ? CurrencyAmount.fromRawAmount(token0, fee0Amount)
        : CurrencyAmount.fromRawAmount(token0, 0)

    const feeValue1 =
      fee1Amount && fee1Amount !== '0'
        ? CurrencyAmount.fromRawAmount(token1, fee1Amount)
        : CurrencyAmount.fromRawAmount(token1, 0)

    const { fee0USD, fee1USD } = calculateFeeUSDValue(fee0Amount, fee1Amount, position, tokenPrices)

    const fees0Display = formatTokenAmountWithUSD(
      fee0Amount,
      position.enrichedToken0.symbol,
      parseInt(position.enrichedToken0.decimals),
      fee0USD
    )

    const fees1Display = formatTokenAmountWithUSD(
      fee1Amount,
      position.enrichedToken1.symbol,
      parseInt(position.enrichedToken1.decimals),
      fee1USD
    )

    return {
      positionValue: value,
      token0Display: t0,
      token1Display: t1,
      feeData: {
        fee0Amount,
        fee1Amount,
        feeValue0,
        feeValue1,
        fees0Display,
        fees1Display,
        token0,
        token1,
      },
    }
  }, [position, tokenPrices, fees])

  const inRange = useMemo(() => isV3PositionInRange(position), [position])

  const badgeConfig = useMemo(
    () => ({
      textColor: POSITION_BADGES.v3.textColor,
      bgColor: POSITION_BADGES.v3.bgColor,
      statusStyle: inRange
        ? { backgroundColor: '#05966933', color: '#6EE7B7' }
        : { backgroundColor: '#FFB82B33', color: '#FCCB5A' },
      statusText: inRange ? 'In Range' : 'Out of Range',
      statusCircleColor: inRange ? '#34D399' : '#FCCB5A',
    }),
    [inRange]
  )

  const { addLiquidityCTA, removeLiquidityCTA } = useMemo(() => {
    const addCTA = {
      label: 'Add',
      to: inRange
        ? ROUTES.liquidity.v3.increase(
            position.sdkPosition.pool.token0.address,
            position.sdkPosition.pool.token1.address,
            position.sdkPosition.pool.fee,
            position.nftId
          )
        : ROUTES.liquidity.v3.add(
            position.sdkPosition.pool.token0.address,
            position.sdkPosition.pool.token1.address,
            position.sdkPosition.pool.fee
          ),
      variant: 'secondary' as const,
    }

    const removeCTA = {
      label: 'Remove',
      to: ROUTES.liquidity.v3.remove(position.nftId),
      variant: 'secondary' as const,
    }

    return { addLiquidityCTA: addCTA, removeLiquidityCTA: removeCTA }
  }, [inRange, position])

  return {
    positionValue,
    token0Display,
    token1Display,
    feeData,
    inRange,
    badgeConfig,
    addLiquidityCTA,
    removeLiquidityCTA,
  }
}
